import { addMonths } from "date-fns";

import type { MembershipRecord, MembershipValidity, UserProfile } from "@/lib/firestore/types";

const clampMonths = (value?: unknown) => {
  const numeric = typeof value === "string" ? Number(value) : value;
  return typeof numeric === "number" && !Number.isNaN(numeric) ? numeric : 0;
};

type FirestoreTimestampLike = {
  seconds: number;
  nanoseconds?: number;
  toDate?: () => Date;
};

const normalizeDate = (value?: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "number") {
    return new Date(value);
  }
  if (typeof value === "object") {
    const ts = value as FirestoreTimestampLike;
    if (typeof ts?.toDate === "function") {
      const date = ts.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof ts?.seconds === "number") {
      return new Date(ts.seconds * 1000);
    }
  }
  return null;
};

export function computeMembershipValidity(
  membership?: MembershipRecord | null,
  now: Date = new Date()
): MembershipValidity {
  if (!membership) {
    return { isValid: false, reason: "missing" };
  }

  const startDate = normalizeDate(membership.start_date);
  if (!startDate) {
    return { isValid: false, reason: "invalid_start_date" };
  }

  const totalMonths = clampMonths(membership.duration_month) + clampMonths(membership.duration_month_free);
  const expiryDate =
    totalMonths > 0 ? addMonths(startDate, totalMonths) : addMonths(startDate, 12); // default to 12 months when missing

  const isValid = now < expiryDate;
  return {
    isValid,
    validUntil: expiryDate.toISOString(),
    startsOn: startDate.toISOString(),
    reason: isValid ? undefined : "expired",
  };
}

type MembershipAware<T> = T & { membership?: MembershipRecord | null; hasValidMembership?: boolean };

export function ensureUserMembershipStatus<T extends MembershipAware<unknown>>(
  user: T,
  now: Date = new Date()
): T & { hasValidMembership: boolean; membershipValidity: MembershipValidity | null } {
  const validity = computeMembershipValidity(user.membership, now);
  const persistedFlag = typeof user.hasValidMembership === "boolean" ? user.hasValidMembership : null;
  const automatic = Boolean((user.membership as (MembershipRecord & { subscription_id?: string }) | undefined)?.subscription_id);
  const hasValidMembership = persistedFlag === false ? false : automatic ? validity.isValid : validity.isValid || Boolean(persistedFlag);

  return {
    ...user,
    hasValidMembership,
    membershipValidity: validity,
  };
}

export function normalizeUserProfile(data: Omit<UserProfile, "hasValidMembership"> & { hasValidMembership?: boolean }) {
  return ensureUserMembershipStatus(data);
}
